import { noop } from 'lodash';

import type { IRect } from '@pressreader/geometry';
import { error } from '@pressreader/logger';

type ResizeObserverCallback = (target: HTMLElement, size: IRect) => void;

function getElementSizeAndPosition(element: HTMLElement): IRect {
    return element.getBoundingClientRect();
}

function createPositionObserver(callback: ResizeObserverCallback): {
    observe: (target: HTMLElement) => void;
    disconnect: () => void;
} {
    const ResizeObserverConstructor = window.ResizeObserver;
    if (!ResizeObserverConstructor) {
        error('ResizeObserver is not supported');
        return {
            observe: noop,
            disconnect: noop,
        };
    }

    function invokeCallback(target: HTMLElement): void {
        callback(target, getElementSizeAndPosition(target));
    }
    const rafScheduled = new Set<ReturnType<typeof requestAnimationFrame>>();

    const resizeObserver = new ResizeObserver(entries => {
        const rafId = requestAnimationFrame(() => {
            rafScheduled.delete(rafId);
            entries.forEach(entry => {
                const target = entry.target;
                if (!(target instanceof globalThis.HTMLElement)) {
                    return;
                }

                invokeCallback(target);
            });
        });
        rafScheduled.add(rafId);
    });

    return {
        observe: (target: HTMLElement) => {
            invokeCallback(target);
            resizeObserver.observe(target);
        },
        disconnect: () => {
            resizeObserver.disconnect();
            rafScheduled.forEach(cancelAnimationFrame);
        },
    };
}

function getElementMargins(element: HTMLElement): { top: number; right: number; bottom: number; left: number } {
    if (!element) {
        return { top: 0, right: 0, bottom: 0, left: 0 };
    }

    const { marginTop, marginRight, marginBottom, marginLeft } = window.getComputedStyle(element);
    const topMarginOfTopChild = getElementMargins(element.firstElementChild as HTMLElement).top;
    const bottomMarginOfBottomChild = getElementMargins(element.lastElementChild as HTMLElement).bottom;

    return {
        top: Math.max(parseInt(marginTop, 10), topMarginOfTopChild),
        right: parseInt(marginRight, 10),
        bottom: Math.max(parseInt(marginBottom, 10), bottomMarginOfBottomChild),
        left: parseInt(marginLeft, 10),
    };
}

function adjustPositionToFitTheScreen({
    elementSizeWithoutMargins,
    elementSizeWithMargins,
    windowSize,
    marginToTheFirstSide,
    marginToTheFarthestSide,
    firstSideCoordinate,
}: {
    elementSizeWithoutMargins: number;
    elementSizeWithMargins: number;
    windowSize: number;
    marginToTheFirstSide: number;
    marginToTheFarthestSide: number;
    firstSideCoordinate: number;
    maximumSize?: number;
}): {
    firstSideCoordinate: number;
    maximumSize?: number;
} {
    const isElementTooWide = elementSizeWithMargins > windowSize;
    if (isElementTooWide) {
        return {
            firstSideCoordinate: marginToTheFirstSide,
            maximumSize: windowSize - marginToTheFirstSide - marginToTheFarthestSide,
        };
    }

    const isElementFitHorizontally = firstSideCoordinate + elementSizeWithoutMargins + marginToTheFarthestSide <= windowSize;
    if (!isElementFitHorizontally) {
        const distanceFromLeftToRightScreenEdge = windowSize - firstSideCoordinate;
        const widthPlusMarginRight = elementSizeWithoutMargins + marginToTheFarthestSide;
        const overflow = widthPlusMarginRight - distanceFromLeftToRightScreenEdge;

        return {
            firstSideCoordinate: firstSideCoordinate - overflow,
        };
    }

    return {
        firstSideCoordinate,
    };
}

function toPixels(value: number): string {
    return `${value ?? 0}px`;
}

function adjustElementHorizontalPosition({
    width,
    marginLeft,
    marginRight,
    left,
    elementStyle,
}: {
    width: number;
    marginLeft: number;
    marginRight: number;
    left: number;
    elementStyle: CSSStyleDeclaration;
}): void {
    const elementWidthWithMargins = width + marginLeft + marginRight;
    const { firstSideCoordinate: leftCoordinateAdjusted, maximumSize: maxWidth } = adjustPositionToFitTheScreen({
        elementSizeWithoutMargins: width,
        elementSizeWithMargins: elementWidthWithMargins,
        windowSize: window.innerWidth,
        marginToTheFirstSide: marginLeft,
        marginToTheFarthestSide: marginRight,
        firstSideCoordinate: left,
    });

    elementStyle.left = toPixels(leftCoordinateAdjusted);
    if (maxWidth) {
        elementStyle.maxWidth = toPixels(maxWidth);
    }
    if (maxWidth < width) {
        elementStyle.overflowX = 'auto';
    }
}

function adjustElementVerticalPosition({
    height,
    marginTop,
    marginBottom,
    top,
    elementStyle,
}: {
    height: number;
    marginTop: number;
    marginBottom: number;
    top: number;
    elementStyle: CSSStyleDeclaration;
}): void {
    const elementHeightWithMargins = height + marginTop + marginBottom;
    const { firstSideCoordinate: topCoordinateAdjusted, maximumSize: maxHeight } = adjustPositionToFitTheScreen({
        elementSizeWithoutMargins: height,
        elementSizeWithMargins: elementHeightWithMargins,
        windowSize: window.innerHeight,
        marginToTheFirstSide: marginTop,
        marginToTheFarthestSide: marginBottom,
        firstSideCoordinate: top,
    });

    elementStyle.top = toPixels(topCoordinateAdjusted);
    if (maxHeight) {
        elementStyle.maxHeight = toPixels(maxHeight);
    }
    if (maxHeight < height) {
        elementStyle.overflowY = 'auto';
    }
}

function adjustPositionAndSizeToFitTheScreen(element: HTMLElement, elementRect: IRect): void {
    const { width, height, left, top } = elementRect;
    const { top: marginTop, right: marginRight, bottom: marginBottom, left: marginLeft } = getElementMargins(element);
    const elementStyle = element.style;

    adjustElementHorizontalPosition({ width, marginLeft, marginRight, left, elementStyle });
    adjustElementVerticalPosition({ height, marginTop, marginBottom, top, elementStyle });
}

function observeAndAdjustElementPositionAndSizeToFitTheScreen(element: HTMLElement): () => void {
    if (!element) {
        throw new Error('Element is not provided');
    }

    const resizeObserver = createPositionObserver(adjustPositionAndSizeToFitTheScreen);

    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
}

export { observeAndAdjustElementPositionAndSizeToFitTheScreen };
